import React, { useState } from "react";
import { Button, Stack, useTheme, SxProps } from "@mui/material";
import { useResponsive } from "@app/hooks";

interface ToggleOption {
  label: string;
  value: string;
}

interface RHFToggleSwitchProps {
  options: ToggleOption[];
  defaultValue?: string;
  onChange?: (value: string) => void;
  width?: string | number;
  height?: string | number;
  sx?: SxProps;
}

export const RHFToggleSwitch = ({
  options,
  defaultValue,
  onChange,
  width,
  height,
  sx = {},
}: RHFToggleSwitchProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const [selected, setSelected] = useState(defaultValue || options[0].value);

  const handleSelect = (value: string) => {
    setSelected(value);
    onChange?.(value);
  };

  const containerWidth = isMobile ? "100%" : width;
  const containerHeight = isMobile ? 36 : height;
  const buttonFontSize = isMobile ? 13 : 14;
  const buttonMinWidth = isMobile ? "50%" : 85;
  const buttonPaddingX = isMobile ? 0.5 : 1;

  return (
    <Stack
      direction="row"
      flexWrap={isMobile ? "wrap" : "nowrap"}
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: theme.palette.grey[200],
        borderRadius: "40px",
        width: containerWidth,
        height: isMobile ? "auto" : containerHeight,
        p: isMobile ? "4px" : "8px",
        gap: isMobile ? "4px" : 0,
        ...sx,
      }}
    >
      {options?.map((opt) => {
        const isSelected = selected === opt?.value;

        return (
          <Button
            key={opt.value}
            onClick={() => handleSelect(opt?.value)}
            disableRipple
            sx={{
              flex: isMobile ? "1 1 32%" : 1,
              borderRadius: "40px",
              textTransform: "none",
              fontSize: buttonFontSize,
              fontWeight: 500,
              height: isMobile ? 32 : "100%",
              px: buttonPaddingX,
              textWrap:'nowrap',
              minWidth: 0, 
              backgroundColor: isSelected
                ? theme.palette.primary.main
                : "transparent",
              color: isSelected
                ? theme.palette.common.white
                : theme.palette.text.primary,
              "&:hover": {
                backgroundColor: isSelected
                  ? theme.palette.primary.dark
                  : "transparent",
              },
            }}
          >
            {opt?.label}
          </Button>
        );
      })}
    </Stack>
  );
};
